import { Injectable } from "@angular/core";
import { BehaviorSubject, combineLatest, EMPTY, from, timer } from "rxjs";
import { concatMap, distinctUntilChanged, map, switchMap } from "rxjs/operators";
import { ApiService } from "source/app/configuration/services/api.service";
import { LocationStateQuery } from "source/app/configuration/state/location-state.query";
import { FeatureFlagItem } from "../feature-flag.models";

/** Same cadence as Web `FeatureFlagService.startPolling` — long-lived operator sessions need fresh flags. */
const FEATURE_FLAG_REFRESH_INTERVAL_MS = 5 * 60 * 1000;

@Injectable({ providedIn: "root" })
export class FeatureFlagService {
	constructor(
		private apiService: ApiService,
		private locationStateQuery: LocationStateQuery,
	) {}

	private flagsSubject = new BehaviorSubject<FeatureFlagItem[]>([]);

	readonly flags$ = this.flagsSubject.asObservable();

	activate = (): void => {
		combineLatest([this.locationStateQuery.rideOpsKey$, this.locationStateQuery.selectOperator$])
			.pipe(
				map(([key, operator]) => [key, operator?.id ?? null] as const),
				distinctUntilChanged((a, b) => a[0] === b[0] && a[1] === b[1]),
				switchMap(([key, operatorId]) => {
					if (key == null || operatorId == null) {
						this.clear();
						return EMPTY;
					}
					return timer(0, FEATURE_FLAG_REFRESH_INTERVAL_MS).pipe(concatMap(() => from(this.load())));
				}),
			)
			.subscribe();
	};

	load = async (): Promise<void> => {
		try {
			const flags = await this.apiService.getFeatureFlags();
			this.flagsSubject.next(flags ?? []);
		} catch {
			this.flagsSubject.next([]);
		}
	};

	isEnabled = (flagName: string): boolean => {
		return this.flagsSubject.getValue().find((f) => f.name === flagName)?.enabled ?? false;
	};

	getFlags = (): FeatureFlagItem[] => {
		return [...this.flagsSubject.getValue()];
	};

	clear = (): void => {
		this.flagsSubject.next([]);
	};
}
